import MineIcon from '@/public/assets/images/mines/mine.png'
import { delay } from '@/helpers/delay'
import { StyledInput } from '@/components/atoms/StyledInput/StyledInput'
import { AmountButtons } from '@/components/atoms/AmountButtons/AmountButtons'
import { CurrencyDropdown } from '@/components/molecules/CurrencyDropdown/CurrencyDropdown'
import { getTokenByDenom } from '@/services/token-service'
import { TokenMoney } from '@ninjablaze/sdk-ts'
import { currenciesSettings, useMinesStore } from './MinesStore'
import { GET_MINE_GAME } from '@/api/query'
import { RulesButton } from '@/components/atoms/RulesButton/RulesButton'
import { MINES_CONTRACT_ADDRESS } from '@/constants/constants'
import { useAudio, SOUNDS } from '@/context/AudioProvider'
import { useSession } from '@/context/SessionProvider'
import { useAppToast } from '@/hooks/useAppToast'
import { Flex, Box, Grid, Button, SimpleGrid, Text } from '@chakra-ui/react'
import { MsgExecuteContractCompat } from '@ninjablaze/sdk-ts'
import { useMutation } from '@tanstack/react-query'
import { useFormContext, Controller, ControllerRenderProps } from 'react-hook-form'
import { GameRules } from 'types/types'
import { useShallow } from 'zustand/react/shallow'
import { useLazyQuery } from '@apollo/client'
import { BigNumberInWei } from '@injectivelabs/utils'
import * as yup from 'yup'
import Image from 'next/image'
import { useBalanceStore } from '@/context/UserBalance'
import { useEffect } from 'react'
import { FiatOrCryptoAmount } from '@/components/atoms/Amount/Amount'
import { useFiatCurrencyStore } from '@/context/FiatCurrencyStore'
import { SupportedCurrencies } from '@ninjablaze/sdk-ts'
import { placeBetTransaction } from '@/services/GameBroadcaster'
import { useTranslation } from 'next-i18next/pages'
import { GameInfo } from '@/components/atoms/GameInfo/GameInfo'

export interface IFormInput {
  amount: string
  mines: string
}

export const MinesForm = () => {
  const { account, sendTransaction, signIn } = useSession()
  const { onError } = useAppToast()
  const { t } = useTranslation()

  const balanceStore = useBalanceStore()

  const {
    control,
    getValues,
    trigger,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<IFormInput>()

  const {
    gameId,
    setGame,
    bet,
    payable,
    isWaitingOpening,
    isButtonActionLoading,
    txsPending,
    setButtonActionLoading,
    denom,
  } = useMinesStore(
    useShallow((state) => ({
      gameId: state.game?.id,
      setGame: state.setGame,
      initialized: state.initialized,
      status: state.gameState?.status,
      bet: state.gameState?.bet,
      payable: state.gameState?.payable,
      isGameStart: state.localGameState.cells.length > 0,
      isWaitingOpening: state.isGameActive && (state.gameState?.cells.length || 0) < state.localGameState.cells.length,
      txsPending: state.txsPending,
      isButtonActionLoading: state.isButtonActionLoading,
      setButtonActionLoading: state.setButtonActionLoading,
      denom: state.denom,
    }))
  )

  useEffect(() => {
    return useBalanceStore.subscribe(() => {
      trigger('amount')
    })
  }, [])

  const isGameActive = useMinesStore(useShallow((state) => state.isGameActive))

  const { play } = useAudio()

  const { mutateAsync: startGame } = useMutation({
    mutationFn: async () => {
      useMinesStore.setState({
        winPanel: undefined,
      })

      if (!account) {
        signIn()
        return
      }

      if (!(await trigger())) {
        return
      }

      setButtonActionLoading(true)

      const { amount, mines } = getValues()

      const tx = await placeBetTransaction({
        contract_addr: MINES_CONTRACT_ADDRESS,
        funds: {
          denom: denom,
          amount: amount,
        },
        msg: {
          mines: Number(mines),
        },
      })

      if (!tx) {
        throw new Error('Something went wrong!')
      }

      const attributes = tx.events?.find((event) => event?.type.includes('game_created'))?.attributes

      const parseCoin = (str: string) => {
        const match = str.match(/(-?\d+)?([a-zA-Z\d\/_]+)/)
        return match ? { amount: match[1] || null, denom: match[2] } : null
      }

      return {
        txHash: tx.txHash,
        game: {
          id: attributes?.find((attribute: any) => attribute.key === 'game_id')?.value,
          bet: parseCoin(attributes?.find((attribute: any) => attribute.key === 'bet')?.value)!.amount!,
          denom: denom,
          mines: attributes?.find((attribute: any) => attribute.key === 'mines')?.value,
          maxCells: attributes?.find((attribute: any) => attribute.key === 'max_cells')?.value,
          maxWin: attributes?.find((attribute: any) => attribute.key === 'max_win')?.value,
          rtp: attributes?.find((attribute: any) => attribute.key === 'rtp')?.value,
        },
      }
    },
    onSuccess: (data) => {
      if (data) {
        setGame(data.game)
        play(SOUNDS.NEW_ROUND)
      }
    },
    onError: () => {
      balanceStore.unLockBalance(true)
      onError()
    },
    onSettled: () => {
      setButtonActionLoading(false)
    },
  })

  const [loadMineGame] = useLazyQuery(GET_MINE_GAME)

  const { mutateAsync: withdraw } = useMutation({
    mutationFn: async () => {
      if (!account) {
        signIn()
        return
      }

      setButtonActionLoading(true)

      while (true) {
        const game = await loadMineGame({ fetchPolicy: 'no-cache', variables: { id: gameId } })
        if (!game) {
          return
        }

        const rounds = game.data?.minesGame?.minesGameRoundsByGameId?.nodes || []
        if (rounds.filter((round) => round!.result === 'PENDING').length === 0) {
          break
        }

        await delay(1000)
      }

      await sendTransaction(
        MsgExecuteContractCompat.fromJSON({
          sender: account!.injectiveAddress,
          contractAddress: MINES_CONTRACT_ADDRESS,
          funds: [],
          msg: {
            payout: {
              game_id: parseInt(gameId!),
            },
          },
        })
      )

      useMinesStore.setState({
        winPanel: {
          payout: new TokenMoney(payable || 0, denom),
          multiplier: new BigNumberInWei(payable || 0).dividedBy(new BigNumberInWei(bet || 0)).toFixed(2),
        },
      })

      delay(10000).then(() =>
        useMinesStore.setState({
          winPanel: undefined,
        })
      )

      setGame(null)
      play(SOUNDS.PLAYER_WON)
      setButtonActionLoading(false)
    },
    onError: () => {
      onError()
      setButtonActionLoading(false)
    },
  })

  const isDisabled = isGameActive || isButtonActionLoading || isWaitingOpening || txsPending
  const { name } = getTokenByDenom(denom)
  const isUsd = useFiatCurrencyStore((s) => s.isUsd())
  const usdcToFiat = useFiatCurrencyStore((s) => s.usdcToFiat)
  const getSymbol = useFiatCurrencyStore((s) => s.getSymbol)
  const isUsdc = getTokenByDenom(denom)?.currency === SupportedCurrencies.USDC
  const maxInfoText =
    isUsdc && !isUsd
      ? t('common.max') + ' ' + usdcToFiat(currenciesSettings.maxValue[denom]).toFixed(2) + ' ' + getSymbol()
      : t('common.max') + ' ' + currenciesSettings.maxValue[denom] + ' ' + name

  useEffect(() => {
    const minValue = currenciesSettings.minValue[denom]
    const { amount } = getValues()
    const amountValue = parseFloat(amount)
    setValue('amount', (minValue * 10).toString())

    // if (isNaN(amountValue)) {
    //   setValue('amount', (minValue * 10).toString())
    //   return
    // }
    // if (amountValue < minValue) {
    //   setValue('amount', minValue.toString())
    // }
  }, [denom])

  const rtp = useMinesStore((state) => Number(state.game?.rtp || state.contractConfig.rtp))

  return (
    <Flex h={'full'} flexShrink={0} p={{ base: 4, sm: 5 }} bg={'ninja.600'} rounded={'2xl'} flexDir={'column'}>
      <Flex h={'full'} justifyContent={'space-between'} flexDir={'column'}>
        <Box>
          <Box hideFrom="md" mb={4}>
            <MinesFormActionButton
              isError={!!Object.keys(errors).length}
              startGame={() => startGame()}
              withdraw={() => withdraw()}
            />
          </Box>

          <FormTitle />
          <Controller
            control={control}
            name="amount"
            rules={{
              required: 'This field is required',
              min: {
                value: currenciesSettings.minValue[denom],
                message: `Minimum bet is ${currenciesSettings.minValue[denom]} ${name}`,
              },
              max: {
                value: currenciesSettings.maxValue[denom],
                message: `Max bet is ${currenciesSettings.maxValue[denom]} ${name}`,
              },
            }}
            render={({ field, formState }) => (
              <Grid mb={{ base: 2, sm: 4 }} gap={2}>
                <StyledInput
                  validate={(value: string) => {
                    const amountSchema = yup.number().positive()
                    const isPositiveInt = amountSchema.isValidSync(value)
                    const isZero = value === '0'
                    const isDecimal =
                      value.length > 1 &&
                      (value.at(-1) === '.' || value.at(-2) === '.') &&
                      value.split('').filter((char) => char === '.').length === 1 &&
                      value.split('.').at(-1)!.length <= 3

                    return !!value.length && !value.includes('-') && (isPositiveInt || isDecimal || isZero)
                  }}
                  icon={getTokenByDenom(denom).icon('18px')}
                  control={field}
                  disabled={isDisabled}
                  errors={formState.errors.amount?.message}
                  placeholder={t('common.enter-amount')}
                  title={t('common.amount')}
                  maxValue={currenciesSettings.maxValue[denom]}
                  clearable={true}
                  infoText={maxInfoText}
                  denom={denom}
                />
                <AmountButtons
                  disabled={isDisabled}
                  maxValue={currenciesSettings.maxValue[denom]}
                  denom={denom}
                  setAmount={(value) => setValue('amount', value, { shouldValidate: true, shouldDirty: true })}
                  amount={watch('amount')}
                />
              </Grid>
            )}
          />
          <Controller
            control={control}
            name="mines"
            rules={{
              required: 'This field is required',
              min: {
                value: 1,
                message: 'Minimum number of mines is 1',
              },
              max: {
                value: 24,
                message: 'Maximum number of mines is 24',
              },
            }}
            render={({ field, fieldState }) => (
              <Flex
                mb={{ base: 2, sm: 4 }}
                rounded={'xl'}
                bg={{ base: 'ninja.500', sm: 'transparent' }}
                flexDir={{ base: 'row', sm: 'column' }}
              >
                <Box mb={{ base: 0, sm: 2 }}>
                  <StyledInput
                    validate={(value) => new RegExp(/^[0-9]*$/).test(value)}
                    icon={<Image width={20} height={20} src={MineIcon} alt={''} />}
                    control={field}
                    disabled={isDisabled}
                    placeholder={t('verify.enter-mines')}
                    title={t('games.mines')}
                    errors={fieldState.error?.message}
                    maxValue={24}
                    inputs={{
                      inputMode: 'numeric',
                    }}
                  />
                </Box>
                <Flex
                  flexShrink={0}
                  alignItems={'center'}
                  display={{ base: 'flex', sm: 'block' }}
                  px={{ base: 3, sm: 0 }}
                >
                  <FormControlMinesButtons control={field} disabled={isDisabled} />
                </Flex>
              </Flex>
            )}
          />
          <Box hideBelow="md" mb={4}>
            <MinesFormActionButton
              isError={!!Object.keys(errors).length}
              startGame={() => startGame()}
              withdraw={() => withdraw()}
            />
          </Box>
          <Box mb={2} hideBelow="md">
            <GameInfo
              rtp={`${rtp * 100}%`}
              maxWin={
                <FiatOrCryptoAmount
                  value={currenciesSettings.maxWin[denom]?.toString() || '50'}
                  denom={denom}
                  wrapperStyles={{ fontSize: 'xs', display: 'inline' }}
                  iconHidden={true}
                />
              }
            />
          </Box>
        </Box>
        <RulesButton
          styles={{
            color: 'ninja.100',
          }}
          game={GameRules.MINES}
        />
        <Box hideFrom="md" mt={2}>
          <GameInfo
            rtp={`${rtp * 100}%`}
            maxWin={
              <FiatOrCryptoAmount
                value={currenciesSettings.maxWin[denom]?.toString() || '50'}
                denom={denom}
                wrapperStyles={{ fontSize: 'xs', display: 'inline' }}
                iconHidden={true}
              />
            }
          />
        </Box>
      </Flex>
    </Flex>
  )
}

const MinesFormActionButton = ({
  startGame,
  withdraw,
  isError,
}: {
  startGame: () => void
  withdraw: () => void
  isError: boolean
}) => {
  const { t } = useTranslation()
  const { gameId, initialized, status, payable, denom, isWaitingOpening, isWithdrawLoading, isGameStart, txsPending } =
    useMinesStore(
      useShallow((state) => ({
        gameId: state.game?.id,
        setGame: state.setGame,
        initialized: state.initialized,
        status: state.gameState?.status,
        bet: state.gameState?.bet,
        payable: state.gameState?.payable,
        denom: state.denom,
        isGameStart: state.localGameState.cells.length > 0,
        isWaitingOpening:
          state.isGameActive && (state.gameState?.cells.length || 0) < state.localGameState.cells.length,
        isWithdrawLoading: state.isButtonActionLoading,
        txsPending: state.txsPending,
      }))
    )

  const isGameActive = useMinesStore((state) => state.isGameActive)

  // TODO
  if (!(gameId && isGameStart && status !== 'lose')) {
    return (
      <Button
        onClick={() => startGame()}
        isLoading={isWithdrawLoading || !initialized}
        isDisabled={isGameActive || isError}
        w={'full'}
        h={'60px'}
        rounded={'xl'}
      >
        {isGameActive ? t('mines.choose-field') : t('mines.start')}
      </Button>
    )
  }

  const isLoading = isWithdrawLoading || isWaitingOpening || txsPending
  let loadingText = isWaitingOpening ? t('mines.sending-transaction') : ''
  loadingText = txsPending ? loadingText : t('mines.waiting-result')
  loadingText = isWithdrawLoading ? t('mines.withdrawing') : loadingText

  const payableMoney = new TokenMoney(payable || '', denom)
  return (
    <Button
      variant={'orange'}
      gap={2}
      onClick={() => withdraw()}
      isLoading={isLoading}
      loadingText={loadingText}
      w={'full'}
      h={'60px'}
      rounded={'xl'}
    >
      {t('mines.payout')}
      <FiatOrCryptoAmount
        currencyStyles={{ color: 'white' }}
        iconMono
        value={payableMoney.humanFormat}
        denom={payableMoney.denom}
      />
    </Button>
  )
}

const MineControlButton = ({
  value,
  disabled,
  control,
}: {
  value: string
  disabled?: boolean
  control: ControllerRenderProps<IFormInput>
}) => {
  const handleSetAmount = (value: string) => {
    control.onChange(value)
  }

  return (
    <Box rounded={'xl'} bg={{ base: 'ninja.600', sm: 'ninja.500' }} pos={'relative'} overflow={'hidden'}>
      <Button
        isDisabled={disabled}
        onClick={() => handleSetAmount(value)}
        h={9}
        pos={'relative'}
        zIndex={10}
        variant={'secondary'}
        transition={'0.2s'}
        w={'full'}
        bg={'transparent'}
        boxShadow={control.value === value ? '0px 0px 12px 0px #FF1B1B inset!important' : 'none'}
        color={control.value === value ? 'white' : 'ninja.300'}
        sx={{
          '+ div': {
            transform: control.value === value ? 'rotate(45deg) translate(-32px, 0)' : 'none',
          },
        }}
        _hover={{
          boxShadow: '0px 0px 12px 0px #FF1B1B inset',
          color: 'white',
          '+ div': {
            transform: 'rotate(45deg) translate(-32px, 0)',
          },
        }}
        border={'none'}
        size={'sm'}
      >
        {value}
      </Button>
      <Box transition={'0.2s'} pos={'absolute'} width={'30px'} height={'30px'} top={'100%'} left={'100%'}>
        <Image src={MineIcon} alt={''} />
      </Box>
    </Box>
  )
}

const FormControlMinesButtons = ({
  disabled,
  control,
}: {
  disabled?: boolean
  control: ControllerRenderProps<IFormInput>
}) => {
  return (
    <SimpleGrid columns={4} gap={1}>
      <MineControlButton disabled={disabled} value={'3'} control={control} />
      <MineControlButton disabled={disabled} value={'5'} control={control} />
      <MineControlButton disabled={disabled} value={'10'} control={control} />
      <MineControlButton disabled={disabled} value={'20'} control={control} />
    </SimpleGrid>
  )
}

const FormTitle = () => {
  const { t } = useTranslation()
  const isDisabled = useMinesStore(
    (state) =>
      state.isGameActive ||
      state.isButtonActionLoading ||
      state.txsPending ||
      (state.isGameActive && (state.gameState?.cells.length || 0) < state.localGameState.cells.length)
  )

  return (
    <Flex justifyContent={'space-between'} alignItems={'center'} mb={3}>
      <Text lineHeight={1} fontSize={'xl'} fontWeight={700}>
        {t('common.your-bet')}
      </Text>
      <CurrencyDropdown isDisabled={isDisabled} />
    </Flex>
  )
}
